import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';

import {
  acceptTransferMatch,
  requestTransferMatches,
} from '#banksync/transfer-match';
import type { TransferMatchItem } from '#banksync/transfer-match';
import { PayeeAutocomplete } from '#components/autocomplete/PayeeAutocomplete';
import { Modal, ModalCloseButton, ModalHeader } from '#components/common/Modal';
import { addNotification } from '#notifications/notificationsSlice';
import { useDispatch } from '#redux';

import { TransferMatchRow } from './TransferMatchRow';

type Props = {
  matches: TransferMatchItem[];
  windowDays: number;
};

function keyOf(item: TransferMatchItem): string {
  return item.from.id + '|' + item.to.id;
}

export function TransferMatchModal({
  matches,
  windowDays: initialWindow,
}: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [items, setItems] = useState(matches);
  const [windowDays, setWindowDays] = useState(initialWindow);
  const [payeeIds, setPayeeIds] = useState<string[]>([]);
  const [rescanning, setRescanning] = useState(false);
  const [accepting, setAccepting] = useState(false);

  async function runScan(nextWindow: number, nextPayeeIds: string[]) {
    const clamped = Math.max(0, Math.min(14, nextWindow));
    setWindowDays(clamped);
    setPayeeIds(nextPayeeIds);
    setRescanning(true);
    try {
      const res = await requestTransferMatches({
        windowDays: clamped,
        payeeIds: nextPayeeIds,
      });
      setItems(res.matches);
    } finally {
      setRescanning(false);
    }
  }

  async function handleAccept(item: TransferMatchItem) {
    const k = keyOf(item);
    try {
      await acceptTransferMatch(item);
      setItems(prev => prev.filter(i => keyOf(i) !== k));
      dispatch(
        addNotification({
          notification: {
            type: 'message',
            message: t('Linked transfer between {{from}} and {{to}}.', {
              from: item.fromAccountName,
              to: item.toAccountName,
            }),
          },
        }),
      );
    } catch {
      dispatch(
        addNotification({
          notification: {
            type: 'error',
            message: t('Failed to link transfer. Please try again.'),
          },
        }),
      );
    }
  }

  function handleDismiss(item: TransferMatchItem) {
    const k = keyOf(item);
    setItems(prev => prev.filter(i => keyOf(i) !== k));
  }

  async function handleAcceptAll() {
    setAccepting(true);
    try {
      for (const item of [...items]) {
        await handleAccept(item);
      }
    } finally {
      setAccepting(false);
    }
  }

  return (
    <Modal name="transfer-matches">
      {({ state }) => (
        <View style={{ width: 'min(760px, 92vw)', flexShrink: 0 }}>
          <ModalHeader
            title={t('Match transfers')}
            rightContent={<ModalCloseButton onPress={() => state.close()} />}
          />

          {/* window control | re-scans across all accounts on change */}
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 8,
              padding: '4px 8px 8px',
              flexShrink: 0,
            }}
          >
            <Text style={{ fontSize: 13, color: theme.pageTextSubdued }}>
              <Trans>Match transactions within</Trans>
            </Text>
            <Button
              isDisabled={rescanning || windowDays <= 0}
              onPress={() => void runScan(windowDays - 1, payeeIds)}
            >
              -
            </Button>
            <Text style={{ fontSize: 13, minWidth: 70, textAlign: 'center' }}>
              <Trans count={windowDays}>± {{ count: windowDays }} days</Trans>
            </Text>
            <Button
              isDisabled={rescanning || windowDays >= 14}
              onPress={() => void runScan(windowDays + 1, payeeIds)}
            >
              +
            </Button>
          </View>

          {/* payee filter | empty = no filter (all payees) */}
          <View
            style={{
              padding: '0 8px 8px',
              flexShrink: 0,
            }}
          >
            <Text
              style={{
                fontSize: 13,
                color: theme.pageTextSubdued,
                marginBottom: 4,
              }}
            >
              <Trans>Limit to payees (optional)</Trans>
            </Text>
            <PayeeAutocomplete
              type="multi"
              value={payeeIds}
              onSelect={(ids: string[]) => void runScan(windowDays, ids)}
              showMakeTransfer={false}
              embedded
            />
          </View>

          <View
            style={{
              maxHeight: '60vh',
              overflowY: 'auto',
              marginBottom: 16,
              flexShrink: 0,
            }}
          >
            {items.length === 0 ? (
              <Text
                style={{
                  padding: 12,
                  fontSize: 13,
                  fontStyle: 'italic',
                  color: theme.pageTextSubdued,
                  flexShrink: 0,
                }}
              >
                <Trans>No transfer matches</Trans>
              </Text>
            ) : (
              items.map(item => (
                <TransferMatchRow
                  key={keyOf(item)}
                  item={item}
                  onAccept={() => void handleAccept(item)}
                  onDismiss={() => handleDismiss(item)}
                />
              ))
            )}
          </View>

          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 8,
              flexShrink: 0,
            }}
          >
            <Text style={{ fontSize: 12, color: theme.pageTextSubdued }}>
              <Trans count={items.length}>
                {{ count: items.length }} potential transfers
              </Trans>
            </Text>
            <View style={{ flexDirection: 'row', gap: 8 }}>
              {items.length > 0 && (
                <Button
                  variant="primary"
                  isDisabled={accepting}
                  onPress={() => void handleAcceptAll()}
                >
                  <Trans>Make all transfers</Trans>
                </Button>
              )}
              <Button onPress={() => state.close()}>
                <Trans>Close</Trans>
              </Button>
            </View>
          </View>
        </View>
      )}
    </Modal>
  );
}
